import { doc, Firestore, getDoc } from "firebase/firestore";

// utilities
import { getGlobalUserAccessKey, isObject } from "wombat-global/src/utilities";

import { firestoreRegionDb } from "./firebase.init";

// types
import { WombatRegionCodes } from "wombat-global/src/typings/multi-region";
import {
  IClientTarget,
  ISiteTarget,
  ITenantTarget,
} from "wombat-global/src/typings";
import { TARGET_META_COLLECTION } from "wombat-global/src/constants";

const targetRegionCache = new Map<string, WombatRegionCodes>();
const targetRegionRequests = new Map<string, Promise<WombatRegionCodes>>();

function getRegionCode(data: unknown): WombatRegionCodes {
  if (!isObject(data) || (data.local !== "us" && data.local !== "ca")) {
    return "us";
  }

  return data.local;
}

async function getTargetRegion(accessKey: string): Promise<WombatRegionCodes> {
  const cachedRegion = targetRegionCache.get(accessKey);
  if (cachedRegion) {
    return cachedRegion;
  }

  const pendingRequest = targetRegionRequests.get(accessKey);
  if (pendingRequest) {
    return pendingRequest.catch(() => "us");
  }

  const ref = doc(
    firestoreRegionDb.us,
    [TARGET_META_COLLECTION, accessKey].join("/"),
  );
  const request = getDoc(ref)
    .then((snapshot) => getRegionCode(snapshot.data()))
    .then((region) => {
      targetRegionCache.set(accessKey, region);
      return region;
    })
    .finally(() => {
      targetRegionRequests.delete(accessKey);
    });

  targetRegionRequests.set(accessKey, request);
  return request.catch(() => "us");
}

/**
 * Split out firebase init so that all utilities that require 'window' are in this `.service`, and others are in `.init`
 */
export * from "./firebase.init";

// export default _firebase;
export async function generateFirestoreConnection(
  local: WombatRegionCodes,
): Promise<Firestore>;
export async function generateFirestoreConnection(
  target: IClientTarget | ISiteTarget | ITenantTarget | undefined,
): Promise<Firestore>;
export async function generateFirestoreConnection(
  target:
    | IClientTarget
    | ISiteTarget
    | ITenantTarget
    | WombatRegionCodes
    | undefined,
): Promise<Firestore> {
  if (!target) {
    return firestoreRegionDb.us;
  }

  if (typeof target === "string") {
    return firestoreRegionDb[target] || firestoreRegionDb.us;
  }

  /**
   *
   * Note: the meta document may not exist. If it doesn't then the region will be the "us".
   *
   * catch is needed for when we are offline
   *
   */
  const region = await getTargetRegion(getGlobalUserAccessKey(target));
  return firestoreRegionDb[region] || firestoreRegionDb.us;
}
